import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Stack,
  Menu,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useNavigate, useParams } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import PercentIcon from '@mui/icons-material/Percent';
import GroupIcon from '@mui/icons-material/Group';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import UICard, { UICardContent, UICardFooter, UICardHeading } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { vestResolver } from '@hookform/resolvers/vest';
import { create, test, enforce } from 'vest';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Form validation
const suite = create('reseller_detail', (data = {}) => {
  test('name', 'Reseller name is required', () => {
    enforce(data.name).isNotEmpty();
  });

  test('region', 'Region is required', () => {
    enforce(data.region).isNotEmpty();
  });

  test('industry', 'Industry is required', () => {
    enforce(data.industry).isNotEmpty();
  });

  test('contactEmail', 'Email is required', () => {
    enforce(data.contactEmail).isNotEmpty();
  });

  test('contactEmail', 'Email must be valid', () => {
    if (data.contactEmail) {
      enforce(data.contactEmail).matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    }
  });

  test('commissionRate', 'Commission rate is required', () => {
    enforce(data.commissionRate).isNotEmpty();
  });

  test('commissionRate', 'Commission rate must be a number between 1 and 40', () => {
    if (data.commissionRate) {
      const rate = Number(data.commissionRate);
      enforce(rate).greaterThanOrEquals(1);
      enforce(rate).lessThanOrEquals(40);
    }
  });
});

// Interfaces
interface ResellerFormValues {
  name: string;
  region: string;
  industry: string;
  status: string;
  tier: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  commissionRate: string;
  paymentTerms: string;
  products: {
    cloudServer: boolean;
    vps: boolean;
    storage: boolean;
    cdn: boolean;
  };
  notes: string;
}

interface CustomerType {
  id: string;
  name: string;
  email: string;
  revenue: string;
  products: string[];
  createTime: string;
}

// Mock reseller data - replace with API call
const getResellerById = (id: string) => {
  return {
    id: id,
    name: 'TechPartners Inc.',
    createTime: '2023-05-15 09:30:00',
    region: 'United States',
    industry: 'Technology',
    status: 'Active',
    tier: 'Premium',
    contactName: 'John Smith',
    contactEmail: 'john.smith@techpartners.com',
    contactPhone: '+1 555-123-4567',
    commissionRate: '25',
    paymentTerms: 'Net 30',
    revenue: '$125,000',
    products: ['Cloud Server', 'VPS', 'Storage'],
    customers: 24,
    notes: 'Strategic partner with strong technical capabilities and market presence.'
  };
};

// Mock customer data for this reseller
const getResellerCustomers = (_resellerId: string): CustomerType[] => {
  return [
    {
      id: '101',
      name: 'Acme Corp',
      email: 'info@acmecorp.com',
      revenue: '$45,000',
      products: ['Cloud Server', 'Storage'],
      createTime: '2023-06-10 14:22:00'
    },
    {
      id: '102',
      name: 'Beta Industries',
      email: 'contact@betaindustries.com',
      revenue: '$32,500',
      products: ['VPS', 'Storage'],
      createTime: '2023-08-22 09:15:00'
    },
    {
      id: '103',
      name: 'Gamma Tech',
      email: 'support@gammatech.com',
      revenue: '$27,000',
      products: ['Cloud Server', 'VPS'],
      createTime: '2023-11-05 11:30:00'
    }
  ];
};

// Convert product array to object format
const convertArrayToProductObject = (products: string[]) => {
  return {
    cloudServer: products.includes('Cloud Server'),
    vps: products.includes('VPS'),
    storage: products.includes('Storage'),
    cdn: products.includes('CDN')
  };
};

export default function ResellerDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  if (!id) {
    navigate('/resellers');
    return null;
  }
  
  const reseller = getResellerById(id);
  const customers = getResellerCustomers(id);
  
  const regions = ['United States', 'Singapore', 'Malaysia', 'Indonesia', 'Vietnam', 'Japan', 'South Korea', 'Germany', 'United Kingdom', 'France'];
  const industries = ['Technology', 'Cloud Services', 'IT Services', 'Telecom', 'Consulting', 'E-commerce', 'Finance'];
  const tiers = ['Standard', 'Premium', 'Elite'];
  const statusOptions = ['Active', 'Pending', 'Suspended'];
  const paymentTermsOptions = ['Net 15', 'Net 30', 'Net 45', 'Net 60'];
  
  // Set up form using React Hook Form with Vest validation
  const methods = useForm<ResellerFormValues>({
    defaultValues: {
      name: reseller.name,
      region: reseller.region,
      industry: reseller.industry,
      status: reseller.status,
      tier: reseller.tier,
      contactName: reseller.contactName,
      contactEmail: reseller.contactEmail,
      contactPhone: reseller.contactPhone,
      commissionRate: reseller.commissionRate,
      paymentTerms: reseller.paymentTerms,
      products: convertArrayToProductObject(reseller.products),
      notes: reseller.notes
    },
    resolver: vestResolver(suite)
  });
  
  const { control, handleSubmit, formState: { errors }, watch } = methods;
  
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/resellers/edit/${id}`);
  };

  const handleDelete = () => {
    handleMenuClose();
    // Delete implementation would go here
    console.log('Delete reseller:', id);
  };

  const handleSuspend = () => {
    handleMenuClose();
    // Suspend implementation would go here
    console.log('Suspend reseller:', id);
  };

  const handleViewPerformance = () => {
    handleMenuClose();
    // View performance implementation would go here
    console.log('View performance for reseller:', id);
  };
  
  const onSubmit = (data: ResellerFormValues) => {
    console.log('Save data:', data);
    // API call would go here
    setIsEditMode(false);
  };
  
  const handleCancel = () => {
    setIsEditMode(false);
    methods.reset({
      name: reseller.name,
      region: reseller.region,
      industry: reseller.industry,
      status: reseller.status,
      tier: reseller.tier,
      contactName: reseller.contactName,
      contactEmail: reseller.contactEmail,
      contactPhone: reseller.contactPhone,
      commissionRate: reseller.commissionRate,
      paymentTerms: reseller.paymentTerms,
      products: convertArrayToProductObject(reseller.products),
      notes: reseller.notes
    });
  };

  const sidebar = React.useMemo(() => ({
    name: '@Resellers',
    uuid: '',
    provider: '',
    paths: [
      {
        name: 'Resellers',
        key: 'resellers',
        slug: '/resellers',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Customers',
        key: 'customers',
        slug: '/customers',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Partners',
        key: 'partners',
        slug: '/partners',
        icon: null,
        disabled: false,
        beta: false,
      }
    ]
  }), []);

  // Watch form values for summary
  const resellerProducts = watch('products');

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Box my="auto" width="75%">
                <Utils.LayoutUtils.MainHeading
                  title={`Reseller: ${reseller.name}`}
                  description={`Onboarded: ${reseller.createTime}`}
                />
              </Box>
              <Stack my="auto" width="25%" direction="row" justifyContent="end">
                <Utils.FormUtils.PrimaryButton
                  variant="text"
                  color="primary"
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleMenuClick}
                >
                  Actions
                </Utils.FormUtils.PrimaryButton>
                
                {isEditMode ? (
                  <Stack direction="row" spacing={1}>
                    <Utils.FormUtils.PrimaryButton onClick={handleCancel}>
                      Cancel
                    </Utils.FormUtils.PrimaryButton>
                    <Utils.FormUtils.PrimaryButton
                      color="primary"
                      onClick={methods.handleSubmit(onSubmit)}
                    >
                      Save
                    </Utils.FormUtils.PrimaryButton>
                  </Stack>
                ) : (
                  <Utils.FormUtils.PrimaryButton
                    color="primary"
                    onClick={() => setIsEditMode(true)}
                  >
                    Edit
                  </Utils.FormUtils.PrimaryButton>
                )}
              </Stack>
            </Stack>
            
            {/* Action Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{ '& .MuiPaper-root': { minWidth: 180 } }}
            >
              <MenuItem onClick={handleViewPerformance}>View Performance</MenuItem>
              <MenuItem onClick={handleEdit}>Edit</MenuItem>
              <MenuItem onClick={handleSuspend}>Suspend</MenuItem>
              <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>Delete</MenuItem>
            </Menu>
            
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item md={8} xs={12}>
                    <UICard gradient>
                      <UICardHeading
                        title="Reseller Overview"
                        description="Basic reseller information and details"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                              <Typography fontSize={12} color={grey[600]}>
                                Reseller ID
                              </Typography>
                              <Typography fontSize={14} fontWeight={500} mb={2}>
                                {reseller.id}
                              </Typography>
                              
                              <Typography fontSize={12} color={grey[600]}>
                                Reseller Name
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <Controller
                                    control={control}
                                    name="name"
                                    render={({ field }) => (
                                      <Utils.FormUtils.PrimaryInput
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.name}
                                </Typography>
                              )}
                              
                              <Typography fontSize={12} color={grey[600]}>
                                Created
                              </Typography>
                              <Typography fontSize={14} fontWeight={500} mb={2}>
                                {reseller.createTime}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                    
                    <UICard gradient>
                      <UICardHeading
                        title="Company Details"
                        description="Reseller company location and industry information"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Region
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <FormControl fullWidth>
                                    <Controller
                                      control={control}
                                      name="region"
                                      render={({ field }) => (
                                        <Utils.FormUtils.PrimarySelect
                                          {...field}
                                          fullWidth
                                          size="small"
                                          error={!!errors.region}
                                        >
                                          {regions.map((region) => (
                                            <Utils.FormUtils.PrimarySelectItem key={region} value={region}>
                                              {region}
                                            </Utils.FormUtils.PrimarySelectItem>
                                          ))}
                                        </Utils.FormUtils.PrimarySelect>
                                      )}
                                    />
                                    {errors.region && (
                                      <FormHelperText error>{errors.region.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.region}
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Industry
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <FormControl fullWidth>
                                    <Controller
                                      control={control}
                                      name="industry"
                                      render={({ field }) => (
                                        <Utils.FormUtils.PrimarySelect
                                          {...field}
                                          fullWidth
                                          size="small"
                                          error={!!errors.industry}
                                        >
                                          {industries.map((industry) => (
                                            <Utils.FormUtils.PrimarySelectItem key={industry} value={industry}>
                                              {industry}
                                            </Utils.FormUtils.PrimarySelectItem>
                                          ))}
                                        </Utils.FormUtils.PrimarySelect>
                                      )}
                                    />
                                    {errors.industry && (
                                      <FormHelperText error>{errors.industry.message}</FormHelperText>
                                    )}
                                  </FormControl>
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.industry}
                                </Typography>
                              )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Status
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <FormControl fullWidth>
                                    <Controller
                                      control={control}
                                      name="status"
                                      render={({ field }) => (
                                        <Utils.FormUtils.PrimarySelect
                                          {...field}
                                          fullWidth
                                          size="small"
                                          error={!!errors.status}
                                        >
                                          {statusOptions.map((status) => (
                                            <Utils.FormUtils.PrimarySelectItem key={status} value={status}>
                                              {status}
                                            </Utils.FormUtils.PrimarySelectItem>
                                          ))}
                                        </Utils.FormUtils.PrimarySelect>
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  <Chip 
                                    label={reseller.status} 
                                    size="small" 
                                    color={reseller.status === 'Active' ? 'success' : 'warning'}
                                  />
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Tier
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <FormControl fullWidth>
                                    <Controller
                                      control={control}
                                      name="tier"
                                      render={({ field }) => (
                                        <Utils.FormUtils.PrimarySelect
                                          {...field}
                                          fullWidth
                                          size="small"
                                          error={!!errors.tier}
                                        >
                                          {tiers.map((tier) => (
                                            <Utils.FormUtils.PrimarySelectItem key={tier} value={tier}>
                                              {tier}
                                            </Utils.FormUtils.PrimarySelectItem>
                                          ))}
                                        </Utils.FormUtils.PrimarySelect>
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.tier}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                    
                    <UICard gradient>
                      <UICardHeading
                        title="Contact Information"
                        description="Reseller contact details"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Contact Name
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <Controller
                                    control={control}
                                    name="contactName"
                                    render={({ field }) => (
                                      <Utils.FormUtils.PrimaryInput
                                        {...field}
                                        fullWidth
                                        size="small"
                                        placeholder="Contact Name"
                                        error={!!errors.contactName}
                                        helperText={errors.contactName?.message}
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.contactName}
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Email
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <Controller
                                    control={control}
                                    name="contactEmail"
                                    render={({ field }) => (
                                      <Utils.FormUtils.PrimaryInput
                                        {...field}
                                        fullWidth
                                        size="small"
                                        placeholder="Email"
                                        error={!!errors.contactEmail}
                                        helperText={errors.contactEmail?.message}
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.contactEmail}
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Phone
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <Controller
                                    control={control}
                                    name="contactPhone"
                                    render={({ field }) => (
                                      <Utils.FormUtils.PrimaryInput
                                        {...field}
                                        fullWidth
                                        size="small"
                                        placeholder="Phone"
                                        error={!!errors.contactPhone}
                                        helperText={errors.contactPhone?.message}
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500}>
                                  {reseller.contactPhone}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                    
                    <UICard gradient>
                      <UICardHeading
                        title="Commercial Terms"
                        description="Commission rates and payment terms"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Commission Rate (%)
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <Controller
                                    control={control}
                                    name="commissionRate"
                                    render={({ field }) => (
                                      <Utils.FormUtils.PrimaryInput
                                        {...field}
                                        fullWidth
                                        size="small"
                                        type="number"
                                        InputProps={{ inputProps: { min: 1, max: 40 } }}
                                        placeholder="Commission Rate"
                                        error={!!errors.commissionRate}
                                        helperText={errors.commissionRate?.message}
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.commissionRate}%
                                </Typography>
                              )}
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} color={grey[600]}>
                                Payment Terms
                              </Typography>
                              {isEditMode ? (
                                <Box mb={2}>
                                  <FormControl fullWidth>
                                    <Controller
                                      control={control}
                                      name="paymentTerms"
                                      render={({ field }) => (
                                        <Utils.FormUtils.PrimarySelect
                                          {...field}
                                          fullWidth
                                          size="small"
                                        >
                                          {paymentTermsOptions.map((term) => (
                                            <Utils.FormUtils.PrimarySelectItem key={term} value={term}>
                                              {term}
                                            </Utils.FormUtils.PrimarySelectItem>
                                          ))}
                                        </Utils.FormUtils.PrimarySelect>
                                      )}
                                    />
                                  </FormControl>
                                </Box>
                              ) : (
                                <Typography fontSize={14} fontWeight={500} mb={2}>
                                  {reseller.paymentTerms}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>
                    
                    <UICard gradient>
                      <UICardHeading
                        title="Products"
                        description="Products this reseller is authorized to sell"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            {isEditMode ? (
                              <>
                                <Grid item xs={12} md={6}>
                                  <Controller
                                    control={control}
                                    name="products.cloudServer"
                                    render={({ field: { value, onChange } }) => (
                                      <FormControlLabel
                                        control={<Checkbox checked={value} onChange={onChange} />}
                                        label="Cloud Server"
                                      />
                                    )}
                                  />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Controller
                                    control={control}
                                    name="products.vps"
                                    render={({ field: { value, onChange } }) => (
                                      <FormControlLabel
                                        control={<Checkbox checked={value} onChange={onChange} />}
                                        label="VPS"
                                      />
                                    )}
                                  />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Controller
                                    control={control}
                                    name="products.storage"
                                    render={({ field: { value, onChange } }) => (
                                      <FormControlLabel
                                        control={<Checkbox checked={value} onChange={onChange} />}
                                        label="Storage"
                                      />
                                    )}
                                  />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Controller
                                    control={control}
                                    name="products.cdn"
                                    render={({ field: { value, onChange } }) => (
                                      <FormControlLabel
                                        control={<Checkbox checked={value} onChange={onChange} />}
                                        label="CDN"
                                      />
                                    )}
                                  />
                                </Grid>
                              </>
                            ) : (
                              <Grid item xs={12}>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {reseller.products.map((product) => (
                                    <Chip 
                                      key={product} 
                                      label={product} 
                                      size="small" 
                                      sx={{ mb: 1 }}
                                    />
                                  ))}
                                </Stack>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>

                    {!isEditMode && (
                      <UICard gradient>
                        <UICardHeading
                          title="Customers"
                          description={`Customers managed by ${reseller.name}`}
                        />
                        <UICardContent>
                          <Box p={2}>
                            <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${grey[200]}` }}>
                              <Table sx={{ minWidth: 650 }} size="small">
                                <TableHead sx={{ backgroundColor: grey[50] }}>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Customer Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Products</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Revenue</TableCell>
                                    <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Created</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {customers.map((customer) => (
                                    <TableRow 
                                      key={customer.id}
                                      hover
                                    >
                                      <TableCell sx={{ fontSize: 13 }}>{customer.name}</TableCell>
                                      <TableCell sx={{ fontSize: 13 }}>{customer.email}</TableCell>
                                      <TableCell sx={{ fontSize: 13 }}>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                          {customer.products.map((product) => (
                                            <Chip 
                                              key={product} 
                                              label={product} 
                                              size="small" 
                                              sx={{ fontSize: 11, mb: 0.5 }}
                                            />
                                          ))}
                                        </Stack>
                                      </TableCell>
                                      <TableCell sx={{ fontSize: 13 }}>{customer.revenue}</TableCell>
                                      <TableCell sx={{ fontSize: 13 }}>{customer.createTime}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        </UICardContent>
                      </UICard>
                    )}
                  </Grid>
                  
                  <Grid item md={4} xs={12}>
                    {isEditMode ? (
                      <UICard width={360} gradient>
                        <UICardHeading
                          title="Reseller Summary"
                          description="Review the reseller configuration"
                        />
                        <UICardContent>
                          <Box p={2}>
                            <Typography fontSize={12} color={grey[600]}>
                              Reseller Name
                            </Typography>
                            <Typography fontSize={14} fontWeight={500} mb={2}>
                              {watch('name') || 'Not specified'}
                            </Typography>
                            
                            <Typography fontSize={12} color={grey[600]}>
                              Region
                            </Typography>
                            <Typography fontSize={14} fontWeight={500} mb={2}>
                              {watch('region') || 'Not specified'}
                            </Typography>
                            
                            <Typography fontSize={12} color={grey[600]}>
                              Industry
                            </Typography>
                            <Typography fontSize={14} fontWeight={500} mb={2}>
                              {watch('industry') || 'Not specified'}
                            </Typography>
                            
                            <Typography fontSize={12} color={grey[600]}>
                              Status
                            </Typography>
                            <Typography fontSize={14} fontWeight={500} mb={2}>
                              {watch('status') || 'Not specified'}
                            </Typography>
                            
                            <Typography fontSize={12} color={grey[600]}>
                              Products
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={2}>
                              {resellerProducts?.cloudServer && <Chip label="Cloud Server" size="small" sx={{ mt: 0.5 }} />}
                              {resellerProducts?.vps && <Chip label="VPS" size="small" sx={{ mt: 0.5 }} />}
                              {resellerProducts?.storage && <Chip label="Storage" size="small" sx={{ mt: 0.5 }} />}
                              {resellerProducts?.cdn && <Chip label="CDN" size="small" sx={{ mt: 0.5 }} />}
                              {!Object.values(resellerProducts || {}).some(v => v) && (
                                <Typography fontSize={14}>No products selected</Typography>
                              )}
                            </Stack>
                          </Box>
                        </UICardContent>
                        <UICardFooter>
                          <Box p={2}>
                            <Stack direction="row" spacing={1}>
                              <Utils.ActionButton
                                text="Cancel"
                                light
                                onClick={handleCancel}
                                sx={{ flex: 1, fontSize: 13 }}
                              />
                              <Utils.ActionButton
                                text="Update"
                                onClick={handleSubmit(onSubmit)}
                                sx={{ flex: 1, fontSize: 13 }}
                              />
                            </Stack>
                          </Box>
                        </UICardFooter>
                      </UICard>
                    ) : (
                      <>
                        <UICard width={360} gradient>
                          <UICardHeading
                            title="Actions"
                            description="Available actions for this reseller"
                          />
                          <UICardContent>
                            <Box p={2}>
                              <Utils.FormUtils.PrimaryButton
                                onClick={() => setIsEditMode(true)}
                                sx={{ width: '100%', mb: 2 }}
                              >
                                Edit Reseller
                              </Utils.FormUtils.PrimaryButton>
                              <Utils.FormUtils.PrimaryButton
                                light
                                onClick={() => navigate('/resellers')}
                                sx={{ width: '100%', mb: 2 }}
                              >
                                Back to Resellers
                              </Utils.FormUtils.PrimaryButton>
                              <Utils.FormUtils.PrimaryButton
                                light
                                onClick={handleDelete}
                                sx={{ width: '100%', color: 'error.main' }}
                              >
                                Delete Reseller
                              </Utils.FormUtils.PrimaryButton>
                            </Box>
                          </UICardContent>
                        </UICard>
                        
                        <UICard width={360} gradient>
                          <UICardHeading
                            title="Performance Metrics"
                            description="Key performance indicators"
                          />
                          <UICardContent>
                            <Box p={2}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <GroupIcon sx={{ color: grey[400], fontSize: 32, mb: 1 }} />
                                    <Typography fontSize={12} color={grey[600]}>
                                      Customers
                                    </Typography>
                                    <Typography fontSize={20} fontWeight={600}>
                                      {reseller.customers}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <AttachMoneyIcon sx={{ color: grey[400], fontSize: 32, mb: 1 }} />
                                    <Typography fontSize={12} color={grey[600]}>
                                      Revenue
                                    </Typography>
                                    <Typography fontSize={20} fontWeight={600}>
                                      {reseller.revenue}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center' }}>
                                    <BusinessIcon sx={{ color: grey[400], fontSize: 32, mb: 1 }} />
                                    <Typography fontSize={12} color={grey[600]}>
                                      Tier
                                    </Typography>
                                    <Typography fontSize={20} fontWeight={600}>
                                      {reseller.tier}
                                    </Typography>
                                  </Box>
                                </Grid>
                                <Grid item xs={6}>
                                  <Box sx={{ textAlign: 'center' }}>
                                    <PercentIcon sx={{ color: grey[400], fontSize: 32, mb: 1 }} />
                                    <Typography fontSize={12} color={grey[600]}>
                                      Commission
                                    </Typography>
                                    <Typography fontSize={20} fontWeight={600}>
                                      {reseller.commissionRate}%
                                    </Typography>
                                  </Box>
                                </Grid>
                              </Grid>
                              <Divider sx={{ my: 2 }} />
                              <Typography fontSize={13} color={grey[800]}>
                                Last updated: {reseller.createTime}
                              </Typography>
                            </Box>
                          </UICardContent>
                        </UICard>
                      </>
                    )}
                    
                    {isEditMode && (
                      <UICard width={360} gradient>
                        <UICardHeading
                          title="Additional Notes"
                          description="Notes and comments about this reseller"
                        />
                        <UICardContent>
                          <Box p={2}>
                            <Controller
                              control={control}
                              name="notes"
                              render={({ field }) => (
                                <Utils.FormUtils.PrimaryInput
                                  {...field}
                                  fullWidth
                                  multiline
                                  rows={4}
                                  placeholder="Enter notes about this reseller"
                                  error={!!errors.notes}
                                  helperText={errors.notes?.message}
                                />
                              )}
                            />
                          </Box>
                        </UICardContent>
                      </UICard>
                    )}
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>
    </Grid>
  );
} 